import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ArrowRight, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';

export default function ScoreExplainer() {
    const navigate = useNavigate();
    const [data, setData] = useState(null);

    useEffect(() => {
        API.get('/autonomy/score-explainer').then(r => setData(r.data)).catch(() => {});
    }, []);

    if (!data || !data.has_profile) return null;

    return (
        <div className="bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm" data-testid="score-explainer">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                    <h3 className="text-base font-medium text-zinc-900 font-['Outfit']">Your Readiness Breakdown</h3>
                </div>
                <div className="text-right">
                    <div className="text-2xl font-bold text-emerald-700 font-['Outfit']">{data.overall_score}%</div>
                    <div className="text-[10px] text-zinc-500">{data.qualification}</div>
                </div>
            </div>

            <div className="space-y-4 mb-5">
                {data.factors.map((f, i) => (
                    <div key={i}>
                        <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-zinc-700">{f.name} <span className="text-zinc-400 text-xs">({f.weight}%)</span></span>
                            <span className="font-medium text-zinc-900">{f.value}</span>
                        </div>
                        <Progress value={f.score} className="h-2 bg-zinc-100" />
                        <p className="text-[11px] text-zinc-500 mt-1">{f.tip}</p>
                    </div>
                ))}
            </div>

            <div className="border-t border-zinc-100 pt-4">
                <div className="text-xs font-medium text-zinc-700 mb-2">Next Steps</div>
                <div className="space-y-2">
                    {data.next_steps.map((step, i) => (
                        <div key={i} className={`flex items-center gap-2 p-2 rounded-lg text-sm ${step.impact === 'high' ? 'bg-amber-50' : step.impact === 'medium' ? 'bg-blue-50' : 'bg-emerald-50'}`}>
                            {step.impact === 'high' ? <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" /> :
                             step.impact === 'low' ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> :
                             <TrendingUp className="w-3.5 h-3.5 text-blue-500 shrink-0" />}
                            <span className="flex-1 text-zinc-700">{step.action}</span>
                            {step.link && (
                                <Button size="sm" variant="ghost" onClick={() => navigate(step.link)} className="text-xs h-6 px-2">
                                    Go <ArrowRight className="w-3 h-3 ml-1" />
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {data.purchase_range && (
                <div className="mt-4 bg-zinc-50 rounded-lg p-3 text-center">
                    <div className="text-[10px] text-zinc-500 mb-0.5">Estimated Purchase Range</div>
                    <div className="text-sm font-semibold text-zinc-900">
                        ${data.purchase_range.low?.toLocaleString()} — ${data.purchase_range.high?.toLocaleString()}
                    </div>
                </div>
            )}
        </div>
    );
}
